import React, { useState } from "react";
import { FilterValue, HvFilterGroup } from "@hitachivantara/uikit-react-core";
import { useTranslation } from "react-i18next";
import {
  useAssignees,
  useIncidentTypes,
  usePriorities,
  useSeverity,
  useStatus,
} from "lib/api";
import { getUserDisplay } from "lib/utils/user";

import useStyles from "./styles";

interface IncidentFilter {
  filters?: number[][];
  onChange: (value: number[][]) => void;
}

const IncidentFilter: React.FC<IncidentFilter> = ({
  filters = [],
  onChange,
}) => {
  const classes = useStyles();
  const { t } = useTranslation(["incident", "common"]);
  const [value, setValue] = useState<number[][]>(filters);

  const { data: incidentTypesData } = useIncidentTypes(undefined, {
    revalidateOnFocus: false,
  });

  const { data: prioritiesData } = usePriorities({
    revalidateOnFocus: false,
  });

  const { data: severityData } = useSeverity({
    revalidateOnFocus: false,
  });

  const { data: statusData } = useStatus({
    revalidateOnFocus: false,
  });

  const { data: assigneeData } = useAssignees({
    revalidateOnFocus: false,
  });

  const incidentTypeDataFilter = incidentTypesData?.data.map(
    (incidentType) => ({
      id: incidentType.pk_inc_type_id,
      name: incidentType.inc_type_name,
    })
  );

  const priorityDataFilter = prioritiesData?.data.map((priority) => ({
    id: priority.pk_prt_id,
    name: priority.prt_name,
  }));

  const severityDataFilter = severityData?.data.map((severity) => ({
    id: severity.pk_svrt_id,
    name: severity.svrt_name,
  }));

  const statusDataFilter = statusData?.data.map((status) => ({
    id: status.pk_status_id,
    name: status.status_name,
  }));

  const assigneeDataFilter = assigneeData?.data.map((user) => {
    return {
      id: user.id,
      name: getUserDisplay(user) as string,
    };
  });

  const filtersData = [
    {
      id: "incidentType",
      name: t("incident:filter.incidentType"),
      data: incidentTypeDataFilter === undefined ? [] : incidentTypeDataFilter,
    },
    {
      id: "priority",
      name: t("incident:filter.priority"),
      data: priorityDataFilter === undefined ? [] : priorityDataFilter,
    },
    {
      id: "severity",
      name: t("incident:filter.severity"),
      data: severityDataFilter === undefined ? [] : severityDataFilter,
    },
    {
      id: "status",
      name: t("incident:filter.status"),
      data: statusDataFilter === undefined ? [] : statusDataFilter,
    },
    {
      id: "assignee",
      name: t("incident:filter.assignee"),
      data: assigneeDataFilter === undefined ? [] : assigneeDataFilter,
    },
  ];

  const onChangeHandler = (event: Event, values: FilterValue) => {
    const newValues = values as number[][];
    setValue(newValues);
    onChange(newValues);
  };

  return (
    <HvFilterGroup
      id="IncidentFilter"
      labels={{
        applyLabel: t("common:filter.apply"),
        cancelLabel: t("common:filter.cancel"),
        clearLabel: t("common:filter.clearFilters"),
        placeholder: t("common:filter.filters"),
        searchBoxPlaceholder: t("common:filter.search"),
        selectAll: t("common:filter.all"),
      }}
      value={value}
      filters={filtersData}
      onChange={onChangeHandler}
      className={classes.filter}
    />
  );
};

export default IncidentFilter;
